/**
 * Shared cascading policy-classification hook (FIX 10/11).
 *
 * Wraps the master-data lookups (Insurance Type → Policy Type / Group →
 * Subgroup) and computes per-field conditional-mandatory UI state via the
 * pure `classificationFieldState` helper:
 *  - parent unselected   → disabled, optional
 *  - parent set, no data → disabled, optional, "No … available" placeholder
 *  - options exist       → enabled, REQUIRED
 */

import type { UseQueryResult } from '@tanstack/react-query';
import {
  useInsuranceTypesLookup,
  useInsuranceGroupsLookup,
  useInsuranceSubgroupsLookup,
  usePolicyTypesLookup,
} from './useProducts';
import { classificationFieldState, type ClassificationFieldState } from './classificationField';

export interface ClassificationOption {
  value: string;
  label: string;
}

export interface ClassificationField {
  options: ClassificationOption[];
  state: ClassificationFieldState;
  isLoading: boolean;
}

function toField(
  parentSelected: boolean,
  query: UseQueryResult<Array<{ id: string; name: string }>>,
  emptyLabel: string,
  defaultLabel: string,
): ClassificationField {
  const options = (query.data || []).map((item) => ({ value: item.id, label: item.name }));
  const state =
    parentSelected && query.isLoading
      ? { disabled: true, required: false, placeholder: 'Loading...' }
      : classificationFieldState(parentSelected, query.data, emptyLabel, defaultLabel);
  return { options, state, isLoading: query.isLoading };
}

export interface PolicyClassification {
  insuranceType: { options: ClassificationOption[]; isLoading: boolean };
  policyType: ClassificationField;
  group: ClassificationField;
  subgroup: ClassificationField;
}

export function usePolicyClassification(insuranceTypeId?: string, groupId?: string): PolicyClassification {
  const typesQuery = useInsuranceTypesLookup();
  const policyTypesQuery = usePolicyTypesLookup(insuranceTypeId, { enabled: !!insuranceTypeId });
  const groupsQuery = useInsuranceGroupsLookup(insuranceTypeId);
  const subgroupsQuery = useInsuranceSubgroupsLookup(groupId);

  return {
    insuranceType: {
      options: (typesQuery.data || []).map((t) => ({ value: t.id, label: t.name })),
      isLoading: typesQuery.isLoading,
    },
    policyType: toField(!!insuranceTypeId, policyTypesQuery, 'No policy type available', 'Select policy type'),
    group: toField(!!insuranceTypeId, groupsQuery, 'No group available', 'Select group'),
    subgroup: toField(!!groupId, subgroupsQuery, 'No subgroup available', 'Select subgroup'),
  };
}
